import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_KEYWORDS_DAILY_STATS_FOR_PROJECT } from '../../../../query/keyword/stats/keys';
import { getKeywordsDailyStatsForProject } from '../../../../../services/monitor/keywords/stats/KeywordsStatsService';
import { ChatType } from '../../../../../types/monitor/ChatType';
import { PeriodType } from '../../../../../types/prompt/PeriodType';
import { ProjectStatsResponse } from '../../../../../services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';
import { MonitorPrompt } from '../../../../../models/monitor/MonitorPrompt';

const EMPTY_RESPONSE: ProjectStatsResponse = {
  monitorPromptsKeywordsStatsResponse: [],
};

export const useGetKeywordsDailyStatsForProject = (
  projectId: number | null,
  chatType: ChatType | null,
  period: PeriodType | null,
  from: Date | null,
  to: Date | null,
  // Not part of the URL (the request returns all project prompts regardless) — the prompts serve two
  // purposes: keying on them makes the table refetch when their content changes (e.g. a PENDING
  // question flips to ACTIVE during the 60s poll), and gating `enabled` on them stops the first fetch
  // from firing before the list loads — the undefined→array key flip used to duplicate an identical
  // request. Mirrors useGetProjectDailyStats so the table stays in step with the BS & Visibility chart.
  monitorPrompts: MonitorPrompt[] | null,
  // MonitorTypes for the ads/shopping columns' server-side merge (mirrors the old featuresSummary
  // `models` filter). Included in the query key so the table refetches when the model filter changes.
  models?: string[]
) => {
  const queryClient = useQueryClient();
  // !!monitorPrompts (not length > 0, unlike useGetProjectDailyStats): the request does not take the
  // prompt list, so a promptless project still gets a valid (empty) response — `length > 0` would
  // leave this query permanently disabled there. The gate only means "the list has loaded".
  const queryEnabled = !!projectId && !!chatType && !!period && !!from && !!to && !!monitorPrompts;

  const fromTimestamp = from?.getTime() ?? null;
  const toTimestamp = to?.getTime() ?? null;

  const {
    data: keywordsDailyStatsForProject,
    isLoading,
    isPlaceholderData,
    isError,
    error,
    refetch,
  } = useQuery<ProjectStatsResponse>({
    queryKey: [
      GET_KEYWORDS_DAILY_STATS_FOR_PROJECT,
      projectId,
      chatType,
      period,
      fromTimestamp,
      toTimestamp,
      monitorPrompts,
      models,
    ],
    queryFn: () => {
      return getKeywordsDailyStatsForProject(projectId!, chatType!, period!, from, to, models);
    },
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    retry: 3,
    retryDelay: 1000,
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    return queryClient.invalidateQueries({
      queryKey: [
        GET_KEYWORDS_DAILY_STATS_FOR_PROJECT,
        projectId,
        chatType,
        period,
        fromTimestamp,
        toTimestamp,
      ],
      refetchType: 'active',
    });
  };

  return {
    keywordsDailyStatsForProject: keywordsDailyStatsForProject ?? EMPTY_RESPONSE,
    isLoading: queryEnabled && isLoading,
    // `keepPreviousData` keeps `isLoading` false after a key change (dates, filters) while the old
    // range is still on screen — `isLoading` alone makes the preview page look frozen, not loading.
    isRefreshing: queryEnabled && isPlaceholderData,
    isError: queryEnabled && isError,
    error: queryEnabled ? error : null,
    refetch,
    invalidate,
  };
};
